import React, { useState, useEffect, useRef, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { X, Loader2, Plus, Link2 } from "lucide-react";
import { toast } from "sonner";
import { cn, clampProductSearchHighlight, filterProductSearchMatches, formatMoneyAr, handleProductSearchHotkey, toNum } from "@/lib/utils";
import { lineImportePeso, roundToPeso } from "@/lib/roundToPeso";
import { presupuestoClienteEditable } from "@/lib/presupuestoClienteEditable";
import { UserData } from "@/components/LoginPage";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { PvNoAsignadoGate } from "@/components/PvNoAsignadoGate";
import { sessionIsElevated, canCreateProveedorFromPersonalizado } from "@/lib/modulePermissions";
import { bloqueaPersonalizadosExistentes } from "@/lib/sessionUser";
import { useDataRefresh, dataRefresh, type RefreshEvent } from "@/lib/dataRefresh";
import { todayYmd } from "./cierreCarousel";
import { PresupuestoCalendario } from "./PresupuestoCalendario";
import {
  etiquetaRango,
  etiquetaSemanaHabil,
  interpretarFechaBusqueda,
  type RangoFecha,
} from "./presupuestoBusqueda";
import {
  FECHA_VENTA_MODAL_TITULO,
  mensajeSeCrearaConFecha,
  nextFechaVentaYmd,
  ventaRequiereAvisoFecha,
} from "./fechaVentaCierre";
import {
  assignedPuntosVentaFromUser,
  vendedorBlockedWithoutPv,
} from "@/lib/pvAsignado";
import { refreshOperatorForSale } from "@/lib/sessionUser";
import {
  authAPI,
  clientesAPI,
  productosAPI,
  puntosVentaAPI,
  ventasAPI,
  Cliente,
  Producto as ProductoAPI,
  Presupuesto,
  PuntoVenta,
  ProductoPersonalizadoCreated,
  isForbiddenError,
  isServerError,
} from "@/lib/api";
import {
  ConfiguradorProductoDialog,
  type ConfiguradorProductoExistente,
} from "@/modules/Stock/ConfiguradorProductoDialog";
import { ClientesABM } from "@/modules/Clientes/ClientesABM";
import {
  formatStockTotalLabel,
  productoEsPersonalizado,
  productoStockActual,
  productoStockReservado,
} from "@/lib/ventaStockDisponible";
import {
  ComboProductosDialog,
} from "./ComboProductosDialog";
import {
  findRegularLine,
  groupComboVisualRows,
  comboVisualCodigo,
  comboVisualNombre,
  comboVisualPrecioSum,
  lineKeyOf,
  presupuestoItemsToVentaLines,
  regularLineKey,
  removeLinesByKey,
  syncComboCantidad,
  syncComboPatch,
  type ComboCapableLine,
} from "@/lib/comboLine";
import {
  shouldClearVentaLines,
  shouldHydrateVentaLinesFromPresupuesto,
} from "./shouldClearVentaLines";

interface LocalProducto {
  id: number;
  codigo: string;
  nombre: string;
  precioBase: number;
  cantidad: number;
  descuento1: number;
  descuento2: number;
  stockDisponible?: number;
  stockActual?: number;
  stockReservado?: number;
  stockControlado?: boolean;
  esPersonalizado?: boolean;
  /** Personalizado diferido desde presupuesto (sin PK real aún). */
  configSnapshot?: Record<string, unknown> | null;
  configHash?: string | null;
  deferred?: boolean;
  lineKey?: string;
  comboGroup?: string | null;
  ivaPorcentaje?: number | null;
}

interface FacturaFormProps {
  onFacturaCreated?: (factura: any) => void;
  user: UserData;
  presupuestos: Presupuesto[];
  facturas?: any[];
  cierres?: any[];
  /** Keep-alive: refetch Predeterminado when Ventas becomes visible again. */
  isActive?: boolean;
}

const VENTA_CLIENTE_SALDO_EVENTS: RefreshEvent[] = ["clientes", "facturas", "ventas"];

export function FacturaForm({ onFacturaCreated, user, presupuestos, facturas: _facturas = [], cierres = [], isActive = true }: FacturaFormProps) {
  const ocultaPersonalizadosExistentes = bloqueaPersonalizadosExistentes(user);
  const [clienteQuery, setClienteQuery] = useState("");
  const [showClienteSuggestions, setShowClienteSuggestions] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);

  const [puntosVenta, setPuntosVenta] = useState<PuntoVenta[]>([]);
  const [selectedPuntoVenta, setSelectedPuntoVenta] = useState<number | "">("");

  const [productos, setProductos] = useState<LocalProducto[]>([]);
  const [productoQuery, setProductoQuery] = useState("");
  const [showProductoSuggestions, setShowProductoSuggestions] = useState(false);
  const [productoHighlight, setProductoHighlight] = useState(0);
  const [cantidad, setCantidad] = useState(1);

  const [usePresupuesto, setUsePresupuesto] = useState(false);
  const [selectedPresupuesto, setSelectedPresupuesto] = useState("");
  const [isConsumidorFinal, setIsConsumidorFinal] = useState(false);
  const [presupuestoQuery, setPresupuestoQuery] = useState("");
  const [showPresupuestoSuggestions, setShowPresupuestoSuggestions] = useState(false);
  const [showFinalized, setShowFinalized] = useState(false); // Toggle to show finalized presupuestos
  const [opcionesPresupuesto, setOpcionesPresupuesto] = useState<Presupuesto[]>([]);
  const [cargandoPresupuestos, setCargandoPresupuestos] = useState(false);
  const [rangoPresupuesto, setRangoPresupuesto] = useState<RangoFecha | null>(null);
  const [presupuestoElegido, setPresupuestoElegido] = useState<Presupuesto | null>(null);
  const [modoEntrega, setModoEntrega] = useState<"domicilio" | "presencial">("domicilio");

  const [foundClientes, setFoundClientes] = useState<Cliente[]>([]);
  const [clienteSearchError, setClienteSearchError] = useState<string | null>(null);
  const [foundProductos, setFoundProductos] = useState<ProductoAPI[]>([]);
  const [searchingClientes, setSearchingClientes] = useState(false);
  const [searchingProductos, setSearchingProductos] = useState(false);

  // H.A.7 — ítem personalizado (buscar existente + crear con wizard)
  const [showPersonalizadoPicker, setShowPersonalizadoPicker] = useState(false);
  const [personalizadoQuery, setPersonalizadoQuery] = useState("");
  const [foundPersonalizados, setFoundPersonalizados] = useState<ProductoAPI[]>([]);
  const [searchingPersonalizados, setSearchingPersonalizados] = useState(false);
  const [wizardPersonalizadoOpen, setWizardPersonalizadoOpen] = useState(false);
  const [showComboDialog, setShowComboDialog] = useState(false);
  const [showNuevoCliente, setShowNuevoCliente] = useState(false);
  const [pvDialogOpen, setPvDialogOpen] = useState(false);
  const [fechaModalOpen, setFechaModalOpen] = useState(false);
  const [fechaAvisoYmd, setFechaAvisoYmd] = useState("");
  const pendingVentaRef = useRef<Record<string, unknown> | null>(null);
  const [operatorUser, setOperatorUser] = useState<UserData>(user);
  const pvBlocked = vendedorBlockedWithoutPv(operatorUser);
  const canPickPv = sessionIsElevated(operatorUser);

  const productoDropdownRef = useRef<HTMLDivElement>(null);
  const clienteDropdownRef = useRef<HTMLDivElement>(null);
  const presupuestoDropdownRef = useRef<HTMLDivElement>(null);
  const selectedClienteRef = useRef<Cliente | null>(null);
  const clienteQueryRef = useRef(clienteQuery);

  const selectedPv = puntosVenta.find((pv) => pv.id === selectedPuntoVenta);
  const selectedSucursalId = Number(selectedPv?.sucursal);
  const sucursalIdForVenta =
    Number.isFinite(selectedSucursalId) && selectedSucursalId > 0
      ? selectedSucursalId
      : null;

  const handleModeChange = (value: string) => {
    const isPresupuesto = value === "presupuesto";
    setUsePresupuesto(isPresupuesto);

    // Close suggestions when switching modes
    setShowPresupuestoSuggestions(false);

    // Always reset form data when switching modes to ensure clean state
    if (!isPresupuesto) {
      setSelectedCliente(null);
      setClienteQuery("");
      setProductos([]);
      setProductoQuery("");
      setSelectedPresupuesto("");
      setPresupuestoQuery("");
      setIsConsumidorFinal(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const loadPuntosVenta = async () => {
      try {
        const result = await refreshOperatorForSale(
          user,
          () => authAPI.getCurrentUser(),
          async () =>
            assignedPuntosVentaFromUser({
              puntosVenta: (await puntosVentaAPI.list()) || [],
            }),
        );
        if (cancelled) return;
        if (result.user) setOperatorUser(result.user as UserData);
        setPuntosVenta(result.list as PuntoVenta[]);
        setSelectedPuntoVenta(result.selected?.id ?? "");
      } catch (e) {
        console.error("Error loading Puntos Venta:", e);
      }
    };
    loadPuntosVenta();
    return () => {
      cancelled = true;
    };
  }, [user, isActive]);

  useEffect(() => {
    const search = async () => {
      if (!clienteQuery.trim() || isConsumidorFinal) return;
      setSearchingClientes(true);
      setClienteSearchError(null);
      try {
        const res = await clientesAPI.lookup({ search: clienteQuery, page_size: 10 });
        // Exclude canonical Consumidor Final (codigo=CF) from autocomplete
        const filteredClients = (res || []).filter(c => c.codigo !== "CF");
        setFoundClientes(filteredClients);
      } catch (e) {
        console.error("Búsqueda contextual de cliente:", e);
        setFoundClientes([]);
        if (isForbiddenError(e)) {
          setClienteSearchError("Sin permiso para buscar clientes.");
        } else if (isServerError(e)) {
          setClienteSearchError("Error del servidor al buscar clientes.");
        } else {
          setClienteSearchError("No se pudieron buscar clientes.");
        }
      }
      finally { setSearchingClientes(false); }
    };
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [clienteQuery, isConsumidorFinal]);

  useEffect(() => {
    selectedClienteRef.current = selectedCliente;
  }, [selectedCliente]);
  useEffect(() => {
    clienteQueryRef.current = clienteQuery;
  }, [clienteQuery]);

  const refreshClienteSaldo = useCallback(() => {
    const selected = selectedClienteRef.current;
    if (selected?.codigo) {
      clientesAPI.lookup({ codigo: selected.codigo }).then((rows) => {
        const fresh = rows.find((c) => c.codigo === selected.codigo) || rows[0];
        if (!fresh) return;
        setSelectedCliente((prev) =>
          prev && prev.codigo === fresh.codigo
            ? { ...prev, ...fresh, saldo: toNum(fresh.saldo) }
            : prev,
        );
      }).catch((e) => {
        if (isForbiddenError(e)) {
          console.error("GET /api/clientes/lookup/ 403 al refrescar saldo", e);
        } else {
          console.error("GET /api/clientes/lookup/ al refrescar saldo", e);
        }
      });
    }
    const q = clienteQueryRef.current;
    if (q.trim() && !isConsumidorFinal) {
      clientesAPI.lookup({ search: q, page_size: 10 }).then((res) => {
        setFoundClientes((res || []).filter((c) => c.codigo !== "CF"));
        setClienteSearchError(null);
      }).catch((e) => {
        if (isForbiddenError(e)) {
          console.error("GET /api/clientes/lookup/ 403 al refrescar sugerencias", e);
          setClienteSearchError("Sin permiso para buscar clientes.");
        } else if (isServerError(e)) {
          console.error("GET /api/clientes/lookup/ 5xx al refrescar sugerencias", e);
          setClienteSearchError("Error del servidor al buscar clientes.");
        } else {
          console.error(e);
        }
      });
    }
  }, [isConsumidorFinal]);
  useDataRefresh(VENTA_CLIENTE_SALDO_EVENTS, refreshClienteSaldo);

  useEffect(() => {
    const search = async () => {
      if (!productoQuery.trim()) {
        setFoundProductos([]);
        return;
      }
      setSearchingProductos(true);
      try {
        const res = await productosAPI.list({
          search: productoQuery,
          ...(ocultaPersonalizadosExistentes ? { esPersonalizado: false } : {}),
        });
        const rows = res || [];
        setFoundProductos(
          ocultaPersonalizadosExistentes
            ? rows.filter((p) => !productoEsPersonalizado(p))
            : rows,
        );
      } catch (e) { console.error(e); }
      finally { setSearchingProductos(false); }
    };
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [productoQuery, ocultaPersonalizadosExistentes]);

  useEffect(() => {
    if (!showPersonalizadoPicker) return;
    const search = async () => {
      setSearchingPersonalizados(true);
      try {
        const res = await productosAPI.list({
          search: personalizadoQuery.trim() || undefined,
          esPersonalizado: true,
        });
        // Defensa: el picker solo debe mostrar personalizados (nunca stock normal).
        const onlyPersonalizados = (res || []).filter(
          (p) =>
            p.esPersonalizado === true ||
            (p as { es_personalizado?: boolean }).es_personalizado === true,
        );
        setFoundPersonalizados(onlyPersonalizados);
      } catch (e) {
        console.error(e);
        setFoundPersonalizados([]);
      } finally {
        setSearchingPersonalizados(false);
      }
    };
    const t = setTimeout(search, personalizadoQuery.trim() ? 300 : 0);
    return () => clearTimeout(t);
  }, [personalizadoQuery, showPersonalizadoPicker]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productoDropdownRef.current && !productoDropdownRef.current.contains(event.target as Node)) {
        setShowProductoSuggestions(false);
      }
      if (clienteDropdownRef.current && !clienteDropdownRef.current.contains(event.target as Node)) {
        setShowClienteSuggestions(false);
      }
      if (presupuestoDropdownRef.current && !presupuestoDropdownRef.current.contains(event.target as Node)) {
        const node = event.target;
        const el = node instanceof Element ? node : null;
        if (!el?.closest("[data-slot=popover-content]")) {
          setShowPresupuestoSuggestions(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!usePresupuesto) return;
    const q = presupuestoQuery.trim();
    const timer = window.setTimeout(async () => {
      setCargandoPresupuestos(true);
      try {
        const fecha = interpretarFechaBusqueda(q);
        const params: Record<string, string | number> = {};
        if (rangoPresupuesto) {
          params.fecha__gte = rangoPresupuesto.desde;
          params.fecha__lte = rangoPresupuesto.hasta;
          if (!showFinalized) params.estado__iexact = "pendiente";
        } else if (fecha) {
          params.fecha = fecha.desde;
          if (!showFinalized) params.estado__iexact = "pendiente";
        } else if (q && presupuestoElegido?.numero !== q) {
          params.search = q;
          if (!showFinalized) params.estado__iexact = "pendiente";
        } else {
          params.ventana = "semana_habil";
          params.incluir_finalizados = showFinalized ? 1 : 0;
        }
        setOpcionesPresupuesto(await ventasAPI.listPresupuestosTodos(params));
      } catch {
        setOpcionesPresupuesto([]);
      } finally {
        setCargandoPresupuestos(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [usePresupuesto, presupuestoQuery, showFinalized, rangoPresupuesto, presupuestoElegido]);

  // F1: no vaciar líneas cuando solo cambia la identidad del array `presupuestos`
  // (refetch al foco). Sí hidratar/limpiar al cambiar modo o presupuesto elegido.
  const prevUsePresupuestoRef = useRef(usePresupuesto);
  const prevSelectedPresupuestoRef = useRef(selectedPresupuesto);
  useEffect(() => {
    const modeChanged = prevUsePresupuestoRef.current !== usePresupuesto;
    const selectedChanged = prevSelectedPresupuestoRef.current !== selectedPresupuesto;
    const selectionOrModeChanged = modeChanged || selectedChanged;
    prevUsePresupuestoRef.current = usePresupuesto;
    prevSelectedPresupuestoRef.current = selectedPresupuesto;

    if (
      shouldHydrateVentaLinesFromPresupuesto({
        usePresupuesto,
        selectedId: selectedPresupuesto,
        selectionOrModeChanged,
      })
    ) {
      const presupuesto =
        (presupuestoElegido?.numero === selectedPresupuesto ? presupuestoElegido : null) ||
        opcionesPresupuesto.find((p) => p.numero === selectedPresupuesto) ||
        presupuestos.find((p) => p.numero === selectedPresupuesto);
      if (presupuesto) {
        if (presupuesto.cliente) {
          const clienteObj =
            typeof presupuesto.cliente === "object"
              ? (presupuesto.cliente as Cliente)
              : null;
          if (clienteObj) {
            setSelectedCliente(clienteObj);
            setClienteQuery(clienteObj.nombre || "");
          }
        }
        setIsConsumidorFinal(false);
        const items = (presupuesto.items || []) as any[];
        setProductos(presupuestoItemsToVentaLines(items) as LocalProducto[]);
      }
    } else if (
      shouldClearVentaLines({
        usePresupuesto,
        selectedId: selectedPresupuesto,
        presupuestosChanged: !selectionOrModeChanged,
      })
    ) {
      setProductos([]);
    }
  }, [usePresupuesto, selectedPresupuesto, presupuestoElegido, opcionesPresupuesto, presupuestos]);

  useEffect(() => {
    if (isConsumidorFinal) {
      setSelectedCliente(null);
      setClienteQuery("");
      setUsePresupuesto(false);
      setSelectedPresupuesto("");
    }
  }, [isConsumidorFinal]);

  const selectedPresupuestoObj =
    (presupuestoElegido?.numero === selectedPresupuesto ? presupuestoElegido : null) ||
    opcionesPresupuesto.find((p) => p.numero === selectedPresupuesto) ||
    presupuestos.find((p) => p.numero === selectedPresupuesto);
  const canEditPresupuestoCliente = Boolean(
    usePresupuesto &&
      selectedPresupuesto &&
      presupuestoClienteEditable(selectedPresupuestoObj as { clienteEditable?: boolean; estado?: string }),
  );
  const lockClienteFromPresupuesto = Boolean(
    usePresupuesto && selectedPresupuesto && !canEditPresupuestoCliente,
  );

  const persistPresupuestoCliente = async (c: Cliente) => {
    const pres = selectedPresupuestoObj;
    if (!canEditPresupuestoCliente || !pres?.id || !c.id) return;
    const currentId = typeof pres.cliente === "object" ? pres.cliente?.id : undefined;
    if (currentId === c.id) return;
    try {
      await ventasAPI.updatePresupuesto(pres.id, { cliente: c.id } as Partial<typeof pres>);
      dataRefresh.emit("presupuestos");
    } catch (e) {
      console.error("No se pudo reasignar el cliente del presupuesto", e);
      toast.error("No se pudo cambiar el cliente del presupuesto");
    }
  };

  const selectCliente = (c: Cliente) => {
    setSelectedCliente(c);
    setClienteQuery(c.nombre);
    setShowClienteSuggestions(false);
    setIsConsumidorFinal(false);
    void persistPresupuestoCliente(c);
  };

  const addProducto = (prod: ProductoAPI) => {
    if (!prod.id) {
      toast.error("Producto inválido (sin ID)");
      return;
    }
    const existing = findRegularLine(productos, prod);
    const precio = roundToPeso(
      typeof prod.precio_final === 'string' ? toNum(prod.precio_final) : toNum(prod.precio_final || prod.precioFinal)
    );
    const nextQty = existing
      ? Math.floor(existing.cantidad + cantidad)
      : Math.floor(Math.max(1, cantidad));

    const stockActual = productoStockActual(prod);
    const stockReservado = productoStockReservado(prod);
    if (existing) {
      const existingKey = lineKeyOf(existing);
      setProductos(productos.map(p => lineKeyOf(p) === existingKey ? {
        ...p,
        cantidad: nextQty,
        stockActual,
        stockReservado,
      } : p));
    } else {
      setProductos([...productos, {
        id: prod.id,
        codigo: prod.codigo,
        nombre: prod.nombre,
        precioBase: precio,
        cantidad: nextQty,
        descuento1: 0,
        descuento2: 0,
        stockActual,
        stockReservado,
        esPersonalizado: productoEsPersonalizado(prod),
        lineKey: regularLineKey(prod.id),
      }]);
    }
    setProductoQuery("");
    setCantidad(1);
    setShowProductoSuggestions(false);
    toast.success("Producto agregado");
  };

  const addComboLines = (lines: ComboCapableLine[]) => {
    setProductos((prev) => [
      ...prev,
      ...lines.map((l) => ({
        id: l.id as number,
        codigo: l.codigo,
        nombre: l.nombre,
        precioBase: roundToPeso(l.precioBase),
        cantidad: l.cantidad,
        descuento1: l.descuento1,
        descuento2: l.descuento2,
        comboGroup: l.comboGroup,
        lineKey: l.lineKey,
        ivaPorcentaje: l.ivaPorcentaje ?? 21,
      })),
    ]);
    setCantidad(1);
    toast.success(`${lines[0]?.nombre || "A"} + ${lines[1]?.nombre || "B"}`);
  };

  const filteredProductos = filterProductSearchMatches(foundProductos, productoQuery);

  /** H.A.7 — mismo flujo addProducto desde creado/existente del configurador. */
  const addFromPersonalizadoSnapshot = (snap: {
    id?: number;
    codigo?: string;
    nombre?: string;
    precioFinal?: string | number | null;
    precioBase?: string | number | null;
  }) => {
    if (!snap.id) {
      toast.error("Producto inválido (sin ID)");
      return;
    }
    addProducto({
      id: snap.id,
      codigo: snap.codigo || "",
      nombre: snap.nombre || "Producto personalizado",
      unidadMedida: "UN",
      precioBase: toNum(snap.precioBase ?? snap.precioFinal),
      ivaPorcentaje: 21,
      gananciaPorcentaje: 0,
      precioFinal: toNum(snap.precioFinal ?? snap.precioBase),
      precio_final: toNum(snap.precioFinal ?? snap.precioBase),
      activo: true,
      esPersonalizado: true,
    });
  };

  const handlePersonalizadoCreated = (result: ProductoPersonalizadoCreated) => {
    addFromPersonalizadoSnapshot({
      id: result.producto.id,
      codigo: result.producto.codigo,
      nombre: result.producto.nombre,
      precioFinal: result.producto.precioFinal ?? result.precioFinal,
      precioBase: result.producto.precioBase,
    });
    setWizardPersonalizadoOpen(false);
    setShowPersonalizadoPicker(false);
  };

  const handlePersonalizadoExistente = (existente: ConfiguradorProductoExistente) => {
    addFromPersonalizadoSnapshot({
      id: existente.id,
      codigo: existente.codigo,
      nombre: existente.nombre,
      precioFinal: existente.precioFinal,
      precioBase: existente.precioBase,
    });
    setWizardPersonalizadoOpen(false);
    setShowPersonalizadoPicker(false);
  };

  const removeProducto = (key: string) => {
    setProductos(removeLinesByKey(productos, key));
  };

  const updateCantidad = (key: string, c: number) => {
    setProductos(syncComboCantidad(productos, key, c));
  };

  const updateDescuento1 = (key: string, d: number) => {
    if (d < 0 || d > 100) return;
    setProductos(syncComboPatch(productos, key, { descuento1: d }));
  };

  const updateDescuento2 = (key: string, d: number) => {
    if (d < 0 || d > 100) return;
    setProductos(syncComboPatch(productos, key, { descuento2: d }));
  };

  const calcularTotalProducto = (p: LocalProducto) => {
    return lineImportePeso({
      precio: p.precioBase,
      cantidad: p.cantidad,
      descuento1: p.descuento1,
      descuento2: p.descuento2,
    });
  };

  const filteredPresupuestos = usePresupuesto ? opcionesPresupuesto : [];

  const calcularTotal = () => {
    return roundToPeso(productos.reduce((total, p) => total + calcularTotalProducto(p), 0));
  };

  const resetForm = () => {
    setClienteQuery("");
    setSelectedCliente(null);
    setProductos([]);
    setProductoQuery("");
    setCantidad(1);
    setUsePresupuesto(false);
    setSelectedPresupuesto("");
    setPresupuestoElegido(null);
    setRangoPresupuesto(null);
    setIsConsumidorFinal(false);
    setPresupuestoQuery("");
    setModoEntrega("domicilio");
  };

  const handleGenerar = async () => {
    if (vendedorBlockedWithoutPv(operatorUser)) {
      setPvDialogOpen(true);
      return;
    }
    if (!isConsumidorFinal && !selectedCliente) {
      toast.error("Seleccione cliente");
      return;
    }
    if (productos.length === 0) {
      toast.error("Agregue productos");
      return;
    }

    // CF canónico: enviar codigo exacto; el BE resuelve/asegura el cliente (no fuzzy).
    const clienteRef = isConsumidorFinal ? "CF" : selectedCliente?.id;

    const fechaHoy = todayYmd();
    const data = {
      cliente: clienteRef,
      ...(selectedPuntoVenta !== "" && selectedPuntoVenta != null
        ? { punto_venta: selectedPuntoVenta }
        : {}),
      fecha: fechaHoy,
      modo_entrega: modoEntrega,
      items: productos.map(p => {
        const base = {
          cantidad: p.cantidad,
          precio_unitario: roundToPeso(Number(p.precioBase)),
          descuento1: p.descuento1,
          descuento2: p.descuento2,
          codigo_producto: p.codigo,
          nombre_producto: p.nombre,
          ...(p.comboGroup ? { combo_group: p.comboGroup } : {}),
          ...(p.ivaPorcentaje != null ? { iva_porcentaje: p.ivaPorcentaje } : {}),
        };
        if (p.deferred || (p.configSnapshot && p.id < 0)) {
          return {
            ...base,
            producto: null,
            config_snapshot: p.configSnapshot || null,
            config_hash: p.configHash || null,
          };
        }
        return {
          ...base,
          producto: p.id,
        };
      }),
      subtotal: roundToPeso(productos.reduce((sum, p) => sum + (Number(p.precioBase) * p.cantidad), 0)),
      total: roundToPeso(calcularTotal()),
      estado: 'Pendiente',
      presupuesto: usePresupuesto && selectedPresupuesto
        ? selectedPresupuestoObj?.id
        : null
    };

    let aplicada = nextFechaVentaYmd(cierres, sucursalIdForVenta, fechaHoy);
    try {
      const st = await ventasAPI.getFechaVenta({
        fecha: fechaHoy,
        ...(typeof selectedPuntoVenta === "number" ? { punto_venta: selectedPuntoVenta } : {}),
        ...(sucursalIdForVenta != null ? { sucursal: sucursalIdForVenta } : {}),
      });
      if (st?.fecha) aplicada = st.fecha;
    } catch {
      /* cache local: skip-forward si GET falla */
    }

    if (ventaRequiereAvisoFecha(fechaHoy, aplicada)) {
      pendingVentaRef.current = data;
      setFechaAvisoYmd(aplicada);
      setFechaModalOpen(true);
      return;
    }

    if (onFacturaCreated) {
      onFacturaCreated(data);
      resetForm();
    }
  };

  const confirmarFechaVentaModal = () => {
    const pending = pendingVentaRef.current;
    pendingVentaRef.current = null;
    setFechaModalOpen(false);
    setFechaAvisoYmd("");
    if (pending && onFacturaCreated) {
      onFacturaCreated(pending);
      resetForm();
    }
  };

  return (
    <div className="space-y-6">
      <PvNoAsignadoGate
        blocked={pvBlocked}
        dialogOpen={pvDialogOpen}
        onDialogOpenChange={setPvDialogOpen}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Información de Venta</h3>
        </div>

        <Tabs value={usePresupuesto ? "presupuesto" : "venta"} onValueChange={handleModeChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="venta">Nueva Venta</TabsTrigger>
            <TabsTrigger value="presupuesto">Desde Presupuesto</TabsTrigger>
          </TabsList>

          <TabsContent value="presupuesto" className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="showFinalized"
                checked={showFinalized}
                onCheckedChange={(c) => setShowFinalized(!!c)}
              />
              <Label htmlFor="showFinalized" className="text-sm text-muted-foreground">Mostrar finalizados</Label>
            </div>
            <div className="relative" ref={presupuestoDropdownRef}>
              <div className="flex gap-2">
                <Input
                  value={presupuestoQuery}
                  onChange={e => {
                    setPresupuestoQuery(e.target.value);
                    setRangoPresupuesto(null);
                    setShowPresupuestoSuggestions(true);
                  }}
                  placeholder="Buscar presupuesto..."
                  onFocus={() => setShowPresupuestoSuggestions(true)}
                />
                <PresupuestoCalendario
                  onChange={(rango) => {
                    setRangoPresupuesto(rango);
                    setPresupuestoQuery("");
                    setShowPresupuestoSuggestions(true);
                  }}
                  onClear={() => setRangoPresupuesto(null)}
                  clearLabel="Semana hábil"
                />
              </div>
              {showPresupuestoSuggestions && (
                <div className="absolute z-10 w-full bg-background border rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                  <div className="px-4 py-2 border-b">
                    <div className="text-sm font-medium">{showFinalized ? "Pendientes y finalizados" : "Pendientes"}</div>
                    <div className="text-xs text-muted-foreground">
                      {rangoPresupuesto
                        ? etiquetaRango(rangoPresupuesto.desde, rangoPresupuesto.hasta)
                        : presupuestoQuery.trim() && presupuestoElegido?.numero !== presupuestoQuery.trim()
                          ? `Búsqueda: ${presupuestoQuery.trim()}`
                          : etiquetaSemanaHabil()}
                    </div>
                  </div>
                  {cargandoPresupuestos ? (
                    <div className="px-4 py-2 text-muted-foreground text-sm">Buscando…</div>
                  ) : filteredPresupuestos.length === 0 ? (
                    <div className="px-4 py-2 text-muted-foreground text-sm">No hay presupuestos</div>
                  ) : filteredPresupuestos.map(pres => (
                    <button key={pres.id} type="button"
                      className="w-full px-4 py-2 text-left hover:bg-muted"
                      onClick={() => {
                        setPresupuestoElegido(pres);
                        setSelectedPresupuesto(pres.numero);
                        setPresupuestoQuery(pres.numero);
                        setShowPresupuestoSuggestions(false);
                      }}>
                      <div className="flex justify-between items-center w-full gap-2">
                        <span className="font-medium">{pres.numero}</span>
                        <span className={`text-xs px-1 py-0.5 rounded ${pres.estado?.toLowerCase() === 'pendiente' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                          {pres.estado || 'pendiente'}
                        </span>
                        <span className="truncate">{pres.cliente?.nombre || 'Sin cliente'}</span>
                        <span className="text-muted-foreground text-sm">{typeof pres.fecha === 'string' ? pres.fecha.split('T')[0] : pres.fecha}</span>
                        <span className="font-bold">${typeof pres.total === 'number' ? pres.total.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : pres.total}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="venta" className="mt-0 h-0 p-0 overflow-hidden" />
        </Tabs>

        <div className="flex items-center space-x-2">
          <Checkbox id="cf" checked={isConsumidorFinal} onCheckedChange={(c) => setIsConsumidorFinal(!!c)} disabled={usePresupuesto} />
          <Label htmlFor="cf">Consumidor Final</Label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="relative" ref={clienteDropdownRef}>
            <Label>Cliente</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={clienteQuery}
                  onChange={e => { setClienteQuery(e.target.value); setShowClienteSuggestions(true); }}
                  placeholder="Buscar cliente..."
                  disabled={isConsumidorFinal || lockClienteFromPresupuesto}
                  onFocus={() => setShowClienteSuggestions(true)}
                />
                {searchingClientes && <Loader2 className="absolute right-2 top-2 h-4 w-4 animate-spin" />}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 whitespace-nowrap"
                disabled={isConsumidorFinal || lockClienteFromPresupuesto}
                onClick={() => setShowNuevoCliente(true)}
              >
                + Cliente
              </Button>
            </div>
            {showClienteSuggestions && !isConsumidorFinal && (
              <div className="absolute z-10 w-full bg-background border rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                {clienteSearchError ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">{clienteSearchError}</div>
                ) : foundClientes.length > 0 ? (
                  foundClientes.map(c => (
                  <button key={c.id} type="button"
                    className="w-full px-4 py-2 text-left hover:bg-muted flex justify-between"
                    onClick={() => selectCliente(c)}>
                    <span>{c.nombre}</span>
                    <span className={c.saldo && c.saldo < 0 ? 'text-red-500' : 'text-green-500'}>
                      $ {formatMoneyAr(c.saldo || 0)}
                    </span>
                  </button>
                  ))
                ) : clienteQuery.trim() && !searchingClientes ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No se encontraron clientes</div>
                ) : null}
              </div>
            )}
          </div>
          <div>
            <Label>Punto de Venta</Label>
            {canPickPv ? (
              <select
                className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                value={selectedPuntoVenta === "" ? "" : String(selectedPuntoVenta)}
                onChange={(e) => {
                  const raw = e.target.value;
                  setSelectedPuntoVenta(raw ? Number(raw) : "");
                }}
              >
                {puntosVenta.map((pv) => (
                  <option key={pv.id} value={pv.id}>
                    {pv.codigo} · {pv.nombre}
                  </option>
                ))}
              </select>
            ) : (
              <div className="p-2 border rounded bg-muted text-muted-foreground text-sm">
                {puntosVenta.find(pv => pv.id === selectedPuntoVenta)?.nombre || "Sel..."}
              </div>
            )}
          </div>
        </div>

        {/* Client Details Panel - Full Width */}
        {selectedCliente && !isConsumidorFinal && (
          <>
            <div className="mt-4 p-4 border border-input rounded-lg bg-muted/50 relative group">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Cliente:</span> {selectedCliente.nombre}
                </div>
                <div>
                  <span className="font-medium">{selectedCliente.cuit ? "CUIT: " : (selectedCliente.dni ? "DNI: " : "CUIT/DNI: ")}</span>
                  {selectedCliente.cuit || selectedCliente.dni || "N/A"}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {selectedCliente.email || "-"}
                </div>
                <div>
                  <span className="font-medium">Teléfono:</span> {selectedCliente.telefono || "-"}
                </div>
                <div>
                  <span className="font-medium">Dirección:</span> {selectedCliente.domicilio || selectedCliente.direccion || "-"}
                </div>
                <div>
                  <span className="font-medium">Saldo: </span>
                  <span className={`font-medium ${selectedCliente.saldo < 0 ? 'text-red-500' : 'text-amber-500'}`}>
                    $ {formatMoneyAr(selectedCliente.saldo ?? 0)}
                  </span>
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 rounded-full hover:bg-destructive/20 hover:text-destructive text-muted-foreground"
                onClick={() => { setSelectedCliente(null); setClienteQuery(""); }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Modo de Entrega */}
            <div className="flex flex-col gap-4 p-4 border rounded-lg bg-background shadow-sm">
              <div className="space-y-2">
                <label className="block text-sm font-medium">Modo de Entrega</label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modoEntrega"
                      value="domicilio"
                      checked={modoEntrega === "domicilio"}
                      onChange={() => setModoEntrega("domicilio")}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Envío a domicilio</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modoEntrega"
                      value="presencial"
                      checked={modoEntrega === "presencial"}
                      onChange={() => setModoEntrega("presencial")}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Retiro presencial</span>
                  </label>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Productos</h3>
        <div className="flex gap-2">
          <div className="flex-1 relative" ref={productoDropdownRef}>
            <Input
              value={productoQuery}
              onChange={e => {
                setProductoQuery(e.target.value);
                setShowProductoSuggestions(true);
                setProductoHighlight(0);
              }}
              placeholder="Buscar producto..."
              onFocus={() => setShowProductoSuggestions(true)}
              onKeyDown={(e) =>
                handleProductSearchHotkey(
                  e,
                  filteredProductos.length,
                  (idx) => {
                    const p = filteredProductos[idx];
                    if (p) addProducto(p);
                  },
                  { index: productoHighlight, setIndex: setProductoHighlight },
                )
              }
            />
            {searchingProductos && <Loader2 className="absolute right-2 top-2 h-4 w-4 animate-spin" />}
            {showProductoSuggestions && (
              <div className="absolute z-10 w-full bg-background border rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                {filteredProductos.map((p, i) => (
                  <button key={p.id} type="button"
                    className={cn(
                      "w-full px-4 py-2 text-left hover:bg-muted flex justify-between gap-2",
                      i === clampProductSearchHighlight(productoHighlight, filteredProductos.length) && "bg-muted",
                    )}
                    onClick={() => addProducto(p)}>
                    <span className="truncate">
                      <span className="text-xs text-muted-foreground mr-2">{p.codigo}</span>
                      {p.nombre}
                    </span>
                    <span className="text-sm text-muted-foreground shrink-0">
                      ${(p.precio_final || p.precioFinal || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })} |
                      {formatStockTotalLabel(productoStockActual(p), productoStockReservado(p))}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <NumberField className="w-24" value={cantidad} onChange={e => setCantidad(Math.max(1, parseInt(e.target.value) || 1))} min={1} />
          <Button
            type="button"
            variant="outline"
            title="Ítem personalizado"
            aria-label="Ítem personalizado"
            onClick={() => {
              if (ocultaPersonalizadosExistentes) {
                setWizardPersonalizadoOpen(true);
                return;
              }
              setPersonalizadoQuery("");
              setFoundPersonalizados([]);
              setShowPersonalizadoPicker(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Pers.
          </Button>
          <Button
            type="button"
            variant="outline"
            title="Combinar productos"
            aria-label="Combinar productos"
            onClick={() => setShowComboDialog(true)}
          >
            <Link2 className="h-4 w-4 mr-1" />
            Combo
          </Button>
        </div>

        {productos.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-input">
            <table className="w-full min-w-[640px]">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-2 px-4 text-sm font-medium">Código</th>
                  <th className="text-left py-2 px-4 text-sm font-medium">Producto</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Cant.</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Desc.</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Desc.</th>
                  <th className="text-right py-2 px-4 text-sm font-medium">Precio Lista</th>
                  <th className="text-right py-2 px-4 text-sm font-medium">Total</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {groupComboVisualRows(productos).map((row) => {
                  const producto = row.legs[0];
                  const key = row.key;
                  const codigo = row.isCombo ? comboVisualCodigo(row.legs) : producto.codigo;
                  const nombre = row.isCombo ? comboVisualNombre(row.legs) : producto.nombre;
                  const precioLista = row.isCombo
                    ? comboVisualPrecioSum(row.legs)
                    : Number(producto.precioBase);
                  const totalLinea = row.legs.reduce((s, p) => s + calcularTotalProducto(p), 0);
                  return (
                  <tr key={key} className="border-t border-input">
                    <td className="py-2 px-4 text-sm whitespace-pre-line">{codigo}</td>
                    <td className="py-2 px-4 text-sm">
                      <span className="block whitespace-normal break-words">{nombre}</span>
                    </td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min="1"
                        step="1"
                        value={producto.cantidad}
                        onChange={(e) => updateCantidad(key, Math.floor(Math.max(1, Number(e.target.value) || 1)))}
                        className="w-16 px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min="0"
                        max="100"
                        value={producto.descuento1}
                        onChange={(e) => updateDescuento1(key, parseFloat(e.target.value) || 0)}
                        className="w-16 px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min="0"
                        max="100"
                        value={producto.descuento2}
                        onChange={(e) => updateDescuento2(key, parseFloat(e.target.value) || 0)}
                        className="w-16 px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-sm text-right">
                      ${precioLista.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 px-4 text-sm text-right font-medium">
                      ${totalLinea.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-2 px-4">
                      <button
                        onClick={() => removeProducto(key)}
                        className={deleteIconButtonClass}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
                <tr className="border-t-2 border-input bg-muted/50">
                  <td colSpan={6} className="py-3 px-4 text-right font-semibold">Total:</td>
                  <td className="py-3 px-4 text-right font-bold text-lg">
                    ${calcularTotal().toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleGenerar} className="bg-green-600 hover:bg-green-700">
          Generar Factura
        </Button>
      </div>

      {/* H.A.7 — buscar personalizado existente o crear con wizard compartido */}
      <Dialog
        open={showPersonalizadoPicker}
        onOpenChange={(open) => {
          setShowPersonalizadoPicker(open);
          if (!open) {
            setPersonalizadoQuery("");
            setFoundPersonalizados([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Ítem personalizado</DialogTitle>
            <DialogDescription>
              Buscá un producto personalizado existente o creá uno nuevo con el
              configurador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Input
                value={personalizadoQuery}
                onChange={(e) => setPersonalizadoQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                autoFocus
              />
              {searchingPersonalizados && (
                <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="border rounded-lg max-h-56 overflow-y-auto">
              {foundPersonalizados.length === 0 && !searchingPersonalizados ? (
                <p className="px-3 py-6 text-sm text-muted-foreground text-center">
                  No hay productos personalizados
                  {personalizadoQuery.trim() ? " con ese criterio" : ""}.
                </p>
              ) : (
                foundPersonalizados.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-muted flex justify-between gap-2 border-b border-border last:border-b-0"
                    onClick={() => {
                      addProducto(p);
                      setShowPersonalizadoPicker(false);
                      setPersonalizadoQuery("");
                      setFoundPersonalizados([]);
                    }}
                  >
                    <span className="text-sm">
                      <span className="font-medium">{p.codigo}</span>
                      {" — "}
                      {p.nombre}
                    </span>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      $
                      {toNum(p.precio_final ?? p.precioFinal).toLocaleString(
                        "es-AR",
                        { minimumFractionDigits: 2 },
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
          <DialogFooter className="sm:justify-end">
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPersonalizadoPicker(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowPersonalizadoPicker(false);
                  setWizardPersonalizadoOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Crear nuevo
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfiguradorProductoDialog
        open={wizardPersonalizadoOpen}
        onOpenChange={setWizardPersonalizadoOpen}
        onCreated={handlePersonalizadoCreated}
        onOpenExistente={handlePersonalizadoExistente}
        existenteActionLabel="Usar en la venta"
        canCreateProveedor={canCreateProveedorFromPersonalizado(user.modulePermissions, {
          isElevated: sessionIsElevated(user),
        })}
      />

      <ComboProductosDialog
        open={showComboDialog}
        onOpenChange={setShowComboDialog}
        initialQty={cantidad}
        onAdd={addComboLines}
      />

      <AlertDialog
        open={fechaModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            pendingVentaRef.current = null;
            setFechaAvisoYmd("");
          }
          setFechaModalOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{FECHA_VENTA_MODAL_TITULO}</AlertDialogTitle>
            <AlertDialogDescription>
              {mensajeSeCrearaConFecha(fechaAvisoYmd)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarFechaVentaModal}>Continuar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showNuevoCliente} onOpenChange={setShowNuevoCliente}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo cliente</DialogTitle>
            <DialogDescription>
              Alta con el mismo ABM de Clientes. El cliente quedará seleccionado en la venta.
            </DialogDescription>
          </DialogHeader>
          {showNuevoCliente && (
            <ClientesABM
              createOnly
              onClienteSaved={(cliente) => {
                selectCliente(cliente);
                setShowNuevoCliente(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div >
  );
}